/**
 * Verifikasi dua langkah (2FA) — pendaftaran dan pengelolaan.
 *
 * Dua jalan masuk ke layar ini:
 *   - WAJIB: staf yang perannya wajib 2FA tetapi belum mendaftar dialihkan ke
 *     sini sesudah login (token mereka terbatas; server menolak rute lain).
 *     Tidak ada tombol "lewati".
 *   - SUKARELA / KELOLA: dari Pengaturan, untuk melihat status, menerbitkan
 *     ulang kode pemulihan, atau (bila perannya tidak wajib) menonaktifkan.
 */
import React from 'react'
import toast from 'react-hot-toast'
import { useApp } from '../context/AppContext'
import { Icon } from '../shared'
import { Btn } from '../shared/components'
import {
  get2faStatus, setup2fa, enable2fa, regenerateRecoveryCodes, disable2fa,
} from '../api/auth.api'

const galat = (err) => err?.response?.data?.error || err?.message || 'Terjadi kesalahan'

function RecoveryCodes({ codes, lang, onDone, doneLabel }) {
  const [saved, setSaved] = React.useState(false)
  const teks = codes.join('\n')
  const salin = async () => {
    try { await navigator.clipboard.writeText(teks); toast.success(lang === 'id' ? 'Disalin' : 'Copied') }
    catch { toast.error(lang === 'id' ? 'Gagal menyalin' : 'Copy failed') }
  }
  const unduh = () => {
    const url = URL.createObjectURL(new Blob([teks + '\n'], { type: 'text/plain' }))
    const a = document.createElement('a')
    a.href = url
    a.download = 'gladhy-kode-pemulihan.txt'
    a.click()
    URL.revokeObjectURL(url)
  }
  return (
    <div className="col gap-3">
      <p className="muted" style={{ margin: 0 }}>
        {lang === 'id'
          ? 'Simpan kode pemulihan ini di tempat aman (pengelola kata sandi atau kertas). Tiap kode hanya berlaku sekali dan menjadi satu-satunya jalan masuk bila ponsel Anda hilang. Kode ini tidak akan ditampilkan lagi.'
          : 'Store these recovery codes somewhere safe (a password manager or paper). Each works once and is the only way in if you lose your phone. They will not be shown again.'}
      </p>
      <div className="card" style={{ padding: 16, background: 'var(--surface-2)', fontFamily: 'var(--font-mono)', fontSize: 14, columnCount: 2, columnGap: 24, lineHeight: 1.9 }}>
        {codes.map((c) => <div key={c}>{c}</div>)}
      </div>
      <div className="row gap-2">
        <Btn onClick={salin}>{lang === 'id' ? 'Salin' : 'Copy'}</Btn>
        <Btn onClick={unduh}>{lang === 'id' ? 'Unduh .txt' : 'Download .txt'}</Btn>
      </div>
      <label className="row gap-2" style={{ alignItems: 'center', fontSize: 13 }}>
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        {lang === 'id' ? 'Saya sudah menyimpan kode pemulihan' : 'I have saved my recovery codes'}
      </label>
      <Btn variant="primary" disabled={!saved} onClick={onDone}>{doneLabel}</Btn>
    </div>
  )
}

export default function MfaSetupScreen() {
  const { lang, user, navigate, finishMfaSetup, logout } = useApp()
  const wajib = Boolean(user?.mfaSetupRequired)

  const [status, setStatus] = React.useState(null)
  const [setup, setSetup] = React.useState(null) // { secret, qrDataUrl }
  const [code, setCode] = React.useState('')
  const [codes, setCodes] = React.useState(null)
  const [busy, setBusy] = React.useState(false)
  const [panel, setPanel] = React.useState(null) // 'regen' | 'disable'
  const [password, setPassword] = React.useState('')

  const muatStatus = React.useCallback(() => get2faStatus().then(setStatus).catch((e) => toast.error(galat(e))), [])
  React.useEffect(() => { muatStatus() }, [muatStatus])

  const mulai = async () => {
    setBusy(true)
    try { setSetup(await setup2fa()) } catch (e) { toast.error(galat(e)) } finally { setBusy(false) }
  }

  const aktifkan = async (e) => {
    e.preventDefault()
    setBusy(true)
    try {
      const data = await enable2fa(code.trim())
      setCodes(data.recoveryCodes)
      setSetup(null)
      setCode('')
    } catch (err) {
      toast.error(galat(err))
      setCode('')
    } finally { setBusy(false) }
  }

  const selesai = async () => {
    setCodes(null)
    if (wajib) await finishMfaSetup()
    else { await muatStatus(); navigate('settings') }
  }

  const terbitkanUlang = async (e) => {
    e.preventDefault()
    setBusy(true)
    try {
      const data = await regenerateRecoveryCodes(password, code.trim())
      setCodes(data.recoveryCodes)
      setPanel(null); setPassword(''); setCode('')
    } catch (err) { toast.error(galat(err)) } finally { setBusy(false) }
  }

  const nonaktifkan = async (e) => {
    e.preventDefault()
    setBusy(true)
    try {
      await disable2fa(password, code.trim())
      toast.success(lang === 'id' ? '2FA dinonaktifkan' : '2FA disabled')
      setPanel(null); setPassword(''); setCode('')
      await muatStatus()
    } catch (err) { toast.error(galat(err)) } finally { setBusy(false) }
  }

  const judul = lang === 'id' ? 'Verifikasi dua langkah (2FA)' : 'Two-step verification (2FA)'

  let isi
  if (codes) {
    isi = (
      <RecoveryCodes codes={codes} lang={lang} onDone={selesai}
        doneLabel={wajib ? (lang === 'id' ? 'Lanjut ke aplikasi' : 'Continue to the app') : (lang === 'id' ? 'Selesai' : 'Done')} />
    )
  } else if (!status) {
    isi = <div className="muted">{lang === 'id' ? 'Memuat…' : 'Loading…'}</div>
  } else if (!status.enabled) {
    isi = !setup ? (
      <div className="col gap-3">
        <p className="muted" style={{ margin: 0 }}>
          {wajib
            ? (lang === 'id'
              ? 'Peran Anda wajib memakai verifikasi dua langkah. Anda akan membutuhkan aplikasi autentikator di ponsel (Google Authenticator, Microsoft Authenticator, Authy, 1Password, dll.).'
              : 'Your role requires two-step verification. You will need an authenticator app on your phone (Google Authenticator, Microsoft Authenticator, Authy, 1Password, etc.).')
            : (lang === 'id'
              ? 'Tambahkan lapisan keamanan kedua: selain kata sandi, masuk butuh kode dari aplikasi autentikator di ponsel Anda.'
              : 'Add a second layer of security: besides your password, signing in needs a code from an authenticator app on your phone.')}
        </p>
        <Btn variant="primary" onClick={mulai} disabled={busy}>{lang === 'id' ? 'Mulai atur 2FA' : 'Set up 2FA'}</Btn>
      </div>
    ) : (
      <form onSubmit={aktifkan} className="col gap-3">
        <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }} className="muted">
          <li>{lang === 'id' ? 'Buka aplikasi autentikator dan pindai kode QR ini.' : 'Open your authenticator app and scan this QR code.'}</li>
          <li>{lang === 'id' ? 'Masukkan kode 6 digit yang tampil di aplikasi.' : 'Enter the 6-digit code the app shows.'}</li>
        </ol>
        <img src={setup.qrDataUrl} alt="QR 2FA" width={220} height={220} style={{ alignSelf: 'center', borderRadius: 8, background: '#fff' }} />
        <div className="muted" style={{ fontSize: 12, textAlign: 'center' }}>
          {lang === 'id' ? 'Tidak bisa memindai? Masukkan kunci ini manual:' : "Can't scan? Enter this key manually:"}
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-1)', marginTop: 4, wordBreak: 'break-all' }}>{setup.secret}</div>
        </div>
        <div className="field">
          <label className="field-label">{lang === 'id' ? 'Kode 6 digit' : '6-digit code'}</label>
          <input className="input" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric"
            autoComplete="one-time-code" maxLength={7} placeholder="123456" autoFocus
            style={{ letterSpacing: 4, fontFamily: 'var(--font-mono)' }} />
        </div>
        <Btn variant="primary" type="submit" disabled={busy || code.trim().length < 6}>
          {busy ? (lang === 'id' ? 'Memverifikasi…' : 'Verifying…') : (lang === 'id' ? 'Aktifkan 2FA' : 'Enable 2FA')}
        </Btn>
      </form>
    )
  } else {
    isi = (
      <div className="col gap-3">
        <div className="row gap-2" style={{ alignItems: 'center' }}>
          <Icon name="check" size={16} />
          <strong>{lang === 'id' ? '2FA aktif' : '2FA is on'}</strong>
          <span className="muted">· {lang === 'id' ? `${status.recoveryCodesRemaining} kode pemulihan tersisa` : `${status.recoveryCodesRemaining} recovery codes left`}</span>
        </div>
        {panel ? (
          <form onSubmit={panel === 'regen' ? terbitkanUlang : nonaktifkan} className="col gap-3">
            <div className="field">
              <label className="field-label">{lang === 'id' ? 'Kata sandi' : 'Password'}</label>
              <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" autoFocus />
            </div>
            <div className="field">
              <label className="field-label">{lang === 'id' ? 'Kode dari aplikasi autentikator' : 'Authenticator code'}</label>
              <input className="input" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={7}
                style={{ letterSpacing: 4, fontFamily: 'var(--font-mono)' }} />
            </div>
            <div className="row gap-2">
              <Btn variant="primary" type="submit" disabled={busy || !password || code.trim().length < 6}>
                {panel === 'regen' ? (lang === 'id' ? 'Terbitkan ulang' : 'Regenerate') : (lang === 'id' ? 'Nonaktifkan' : 'Disable')}
              </Btn>
              <Btn type="button" onClick={() => { setPanel(null); setPassword(''); setCode('') }}>{lang === 'id' ? 'Batal' : 'Cancel'}</Btn>
            </div>
          </form>
        ) : (
          <div className="row gap-2">
            <Btn onClick={() => setPanel('regen')}>{lang === 'id' ? 'Terbitkan ulang kode pemulihan' : 'Regenerate recovery codes'}</Btn>
            {!status.required && <Btn onClick={() => setPanel('disable')}>{lang === 'id' ? 'Nonaktifkan 2FA' : 'Disable 2FA'}</Btn>}
          </div>
        )}
        {status.required && (
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>
            {lang === 'id' ? 'Peran Anda wajib 2FA, jadi tidak bisa dinonaktifkan sendiri. Bila ponsel hilang, minta pemilik platform mereset.' : 'Your role requires 2FA, so you cannot disable it yourself. If you lose your phone, ask the platform owner to reset it.'}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="main-inner" style={{ maxWidth: 520, margin: '0 auto', padding: '48px 20px' }}>
      <div className="eyebrow">{wajib ? (lang === 'id' ? 'Wajib' : 'Required') : (lang === 'id' ? 'Keamanan akun' : 'Account security')}</div>
      <h1 className="h1" style={{ marginTop: 6, marginBottom: 20 }}>{judul}</h1>
      <div className="card" style={{ padding: '22px 24px' }}>{isi}</div>
      <div style={{ marginTop: 16 }}>
        {wajib
          ? <button type="button" onClick={logout} className="muted" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12 }}>{lang === 'id' ? 'Keluar' : 'Sign out'}</button>
          : <button type="button" onClick={() => navigate('settings')} className="muted" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12 }}>← {lang === 'id' ? 'Kembali ke Pengaturan' : 'Back to Settings'}</button>}
      </div>
    </div>
  )
}
